import { useEffect, useState } from "react";
import { FolderKanban, Warehouse, Users, Shield, Package, CheckCircle2, AlertTriangle, Truck, ClipboardCheck, Wrench, Undo2, FileCheck, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { Button } from "../components/ui/button";
import CommercialWorkSummary from "../components/CommercialWorkSummary";
import GroundAgeing from "../components/GroundAgeing";
import DprDashboardSummary from "../components/DprDashboardSummary";
import DprActivityTrend from "../components/DprActivityTrend";
import ActionRequired from "../components/ActionRequired";
import MyWorkActivity from "../components/MyWorkActivity";
import AllocationAgeingBlocks from "../components/AllocationAgeingBlocks";
import FieldWorkspace from "./FieldWorkspace";

function KpiCard({ icon: Icon, label, value, tone = "teal", sub, onClick }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    teal: "bg-teal-50 text-teal-700",
    cyan: "bg-cyan-50 text-cyan-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    sky: "bg-sky-50 text-sky-600",
    indigo: "bg-teal-50 text-teal-700",
    blue: "bg-cyan-50 text-cyan-600",
  };
  return (
    <div onClick={onClick} className={`flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3 shadow-sm transition-shadow hover:shadow-md ${onClick ? "cursor-pointer" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-md ${tones[tone] || tones.teal}`}><Icon className="h-4.5 w-4.5" /></div>
      <div className="min-w-0">
        <div className="font-display text-lg font-bold leading-tight text-slate-900 tabular-nums break-all">{value ?? 0}</div>
        <div className="break-words text-xs leading-tight text-slate-500">{label}</div>
        {sub && <div className="truncate text-[10px] text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, has } = useAuth();
  // Field users get a focused, action-oriented workspace as their home instead of the org dashboard.
  const fieldOnly = has("FIELD_MATERIAL.VIEW") && !has("MATERIAL.VIEW");
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [matTypes, setMatTypes] = useState([]);
  const [matType, setMatType] = useState("");
  const [typeData, setTypeData] = useState(null);
  const [secSpin, setSecSpin] = useState({});
  const [secKey, setSecKey] = useState({});

  const fmtTs = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

  const snapParams = () => ({ ...(pid ? { project_id: pid } : {}), ...(!pid && includeInactive ? { include_inactive: true } : {}) });
  const loadSnapshot = () => api.get("/dashboard/snapshot", { params: snapParams() }).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { loadSnapshot(); /* eslint-disable-next-line */ }, [pid, includeInactive]);
  useEffect(() => { api.get("/material-types", { params: { page_size: 500 } }).then((r) => setMatTypes(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!matType) { setTypeData(null); return; }
    api.get("/dashboard/lifecycle-by-type", { params: { ...snapParams(), material_type_id: matType } })
      .then((r) => setTypeData(r.data)).catch(() => setTypeData(null));
    /* eslint-disable-next-line */
  }, [matType, pid, includeInactive]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: res } = await api.post("/dashboard/refresh", null, { params: snapParams() });
      if (res.status === "refreshing") {
        toast.success(res.message);
        // Poll for the new snapshot while the user keeps working.
        let tries = 0;
        const prev = data?.last_updated;
        const iv = setInterval(async () => {
          tries += 1;
          try { const { data: s } = await api.get("/dashboard/snapshot", { params: snapParams() }); if (s.last_updated !== prev || tries >= 8) { setData(s); clearInterval(iv); setRefreshing(false); } }
          catch { clearInterval(iv); setRefreshing(false); }
        }, 3000);
      } else { toast.info(res.message); setRefreshing(false); }
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); setRefreshing(false); }
  };

  // Per-section refresh: snapshot-backed sections re-pull the latest snapshot (and the typed
  // lifecycle view when a material type is selected); self-fetching sections remount to re-run
  // their own live queries — no whole-dashboard reload needed.
  const SecRefresh = ({ k }) => (
    <button type="button" data-testid={`dashboard-refresh-${k}`} title="Refresh this section" aria-label="Refresh this section"
      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-teal-700"
      onClick={async () => {
        setSecSpin((s) => ({ ...s, [k]: true }));
        try {
          if (["org", "lifecycle", "action", "ageing"].includes(k)) {
            await loadSnapshot();
            if (matType) {
              try { const r = await api.get("/dashboard/lifecycle-by-type", { params: { ...snapParams(), material_type_id: matType } }); setTypeData(r.data); } catch { /* keep old */ }
            }
          } else {
            setSecKey((s) => ({ ...s, [k]: (s[k] || 0) + 1 }));
          }
        } finally { setTimeout(() => setSecSpin((s) => ({ ...s, [k]: false })), 500); }
      }}>
      <RefreshCw className={`h-3.5 w-3.5 ${secSpin[k] ? "animate-spin" : ""}`} />
    </button>
  );

  const org = data?.org || {};
  const typed = !!(matType && typeData);
  const mat = typed ? typeData.materials : (data?.materials || {});
  const arData = typed ? { ...data, materials: typeData.materials, ground_ageing: { ...(data?.ground_ageing || {}), overdue: typeData.overdue } } : data;
  const typeOptions = [{ value: "", label: "All Types" }, ...matTypes.map((t) => ({ value: t.id, label: t.type_name }))];

  if (fieldOnly) return <FieldWorkspace />;

  // Whole-page gate: a non-field user without Dashboard access sees a friendly notice.
  if (!has("DASHBOARD.VIEW")) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center" data-testid="dashboard-no-access">
        <Shield className="mb-3 h-10 w-10 text-slate-300" />
        <h2 className="font-display text-lg font-semibold text-slate-700">Dashboard access not enabled</h2>
        <p className="mt-1 max-w-sm text-sm text-slate-500">Your role doesn't have Dashboard access. Please contact an administrator to grant the <span className="font-medium">Dashboard · View</span> permission.</p>
      </div>
    );
  }

  const showOrg = has("DASHBOARD.VIEW_ORG");
  const showLifecycle = has("DASHBOARD.VIEW_LIFECYCLE");
  const showActionRequired = has("DASHBOARD.VIEW_ACTION_REQUIRED");
  const showCommercial = has("DASHBOARD.VIEW_COMMERCIAL");
  const showGroundAgeing = has("DASHBOARD.VIEW_GROUND_AGEING");
  const showDprSummary = has("DASHBOARD.VIEW_DPR_SUMMARY");
  const showDprTrend = has("DASHBOARD.VIEW_DPR_TREND");

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user?.full_name?.split(" ")[0] || "User"}`}
        subtitle="Organization & material lifecycle overview"
        actions={
          <div className="flex items-center gap-3" data-testid="dashboard-refresh-bar">
            {!pid && (
              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-600" data-testid="dashboard-include-inactive">
                <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} className="h-3.5 w-3.5 accent-[var(--mf-purple-600)]" />
                Include inactive projects
              </label>
            )}
            <span className="flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Last Updated: <span data-testid="dashboard-last-updated" className="font-medium text-slate-700">{fmtTs(data?.last_updated)}</span></span>
            <Button data-testid="dashboard-refresh-btn" variant="outline" size="sm" onClick={doRefresh} disabled={refreshing || data?.refreshing}>
              <RefreshCw className={`mr-1 h-4 w-4 ${(refreshing || data?.refreshing) ? "animate-spin" : ""}`} /> {(refreshing || data?.refreshing) ? "Refreshing…" : "Refresh Dashboard"}
            </Button>
          </div>
        }
      />
      <ProjectScopeBanner />

      {showOrg && (
        <>
          <div className="mb-3 flex items-center gap-2"><h2 className="font-display text-lg font-semibold text-slate-900">Organization</h2><SecRefresh k="org" /></div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="dashboard-section-org">
            <KpiCard icon={FolderKanban} label="Active Projects" value={org.projects_active} tone="teal" />
            <KpiCard icon={Warehouse} label="Stores" value={org.stores_total} tone="cyan" />
            <KpiCard icon={Users} label="Users" value={org.users_total} tone="slate" />
            <KpiCard icon={Shield} label="Roles" value={org.roles_total} tone="green" />
          </div>
        </>
      )}

      {(showLifecycle || showActionRequired) && (
        <div className="mb-1 mt-8 flex flex-wrap items-center justify-between gap-2" data-testid="dashboard-type-filter">
          <span className="text-xs font-medium text-slate-500">Showing counts for: <span className="font-semibold text-slate-700">{typed ? (matTypes.find((t) => t.id === matType)?.type_name || "Selected type") : "All material types"}</span></span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Material Type</span>
            <div className="w-56"><SearchableSelect testid="dashboard-mat-type" placeholder="All Types" value={matType} options={typeOptions} onChange={setMatType} /></div>
          </div>
        </div>
      )}

      {showLifecycle && (
        <>
          <div className="mb-3 mt-4 flex items-center gap-2"><h2 className="font-display text-lg font-semibold text-slate-900">Material Lifecycle</h2><SecRefresh k="lifecycle" /></div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8" data-testid="dashboard-section-lifecycle">
            <KpiCard icon={Package} label="GRN Pending" value={mat.grn_pending} tone="teal" />
            <KpiCard icon={CheckCircle2} label="GRN Completed" value={mat.grn_completed} tone="green" />
            <KpiCard icon={AlertTriangle} label="Defective" value={mat.defective} tone="red" />
            <KpiCard icon={Truck} label="Assigned" value={mat.assigned} tone="cyan" />
            <KpiCard icon={ClipboardCheck} label="Acknowledged" value={mat.acknowledged} tone="green" />
            <KpiCard icon={Wrench} label="Installed" value={mat.installed} tone="sky" />
            <KpiCard icon={Undo2} label="Return Requested" value={mat.return_requested} tone="amber" />
            <KpiCard icon={FileCheck} label="Completed (Data Entry)" value={mat.completed_data_entry} tone="teal" />
          </div>
        </>
      )}

      <div className="relative" key={`mywork-${secKey.mywork || 0}`}>
        <div className="absolute right-2 top-2 z-10"><SecRefresh k="mywork" /></div>
        <MyWorkActivity />
      </div>
      {showActionRequired && (
        <div className="relative">
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="action" /></div>
          <ActionRequired data={arData} />
        </div>
      )}
      {showActionRequired && (
        <div className="relative" key={`allocage-${secKey.allocage || 0}`}>
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="allocage" /></div>
          <AllocationAgeingBlocks />
        </div>
      )}
      {showCommercial && (
        <div className="relative" key={`commercial-${secKey.commercial || 0}`}>
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="commercial" /></div>
          <CommercialWorkSummary />
        </div>
      )}
      {showGroundAgeing && (
        <div className="relative">
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="ageing" /></div>
          <GroundAgeing data={data?.ground_ageing} />
        </div>
      )}
      {showDprSummary && (
        <div className="relative" key={`dpr-${secKey.dprsum || 0}`}>
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="dprsum" /></div>
          <DprDashboardSummary />
        </div>
      )}
      {showDprTrend && (
        <div className="relative" key={`dprtrend-${secKey.dprtrend || 0}`}>
          <div className="absolute right-2 top-2 z-10"><SecRefresh k="dprtrend" /></div>
          <DprActivityTrend />
        </div>
      )}

    </div>
  );
}
